import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import moment from "moment";

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmé",
  waitPayment: "En attente paiement",
  paymentConfirmed: "Paiement confirmé",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  waitPayment: "bg-orange-100 text-orange-800 border-orange-200",
  paymentConfirmed: "bg-green-100 text-green-800 border-green-200",
};

const BUDGET_OPTIONS = [
  { label: "Moins de 30 €", value: "<30" },
  { label: "31 - 100 €", value: "31-100" },
  { label: "101 - 250 €", value: "101-250" },
  { label: "251 - 500 €", value: "251-500" },
  { label: "Plus de 500 €", value: ">500" },
];

function InlineEdit({ value, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || "");
  const inputRef = useRef(null);

  const handleSave = () => {
    setEditing(false);
    if (text.trim() !== (value || "")) {
      onSave(text.trim());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") { setText(value || ""); setEditing(false); }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#801111] focus:border-[#801111]"
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="cursor-text hover:bg-gray-100 rounded px-1 py-0.5 -mx-1 transition-colors"
      title="Cliquer pour modifier"
    >
      {value || <span className="text-gray-400 italic">—</span>}
    </span>
  );
}

export default function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [updatingId, setUpdatingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    async function fetchCategories() {
      const catSnap = await getDocs(collection(db, "categories"));
      setCategories(catSnap.docs.map((d) => d.data().name));
    }

    fetchCategories();
  }, []);

  useEffect(() => {
    async function fetchProjects() {
      setLoading(true);
      const snap = await getDocs(collection(db, "projects"));
      const projectsData = [];

      for (const d of snap.docs) {
        const p = d.data();
        let clientEmail = "";
        if (p.userId) {
          const userSnap = await getDoc(doc(db, "users", p.userId));
          if (userSnap.exists()) {
            clientEmail = userSnap.data().email || "";
          }
        }
        projectsData.push({
          id: d.id,
          project: p.project,
          clientEmail,
          budget: p.budget,
          createdAt: p.createdAt,
          status: p.status,
          type: p.type,
        });
      }

      // Trier par date de création (plus récent en premier)
      projectsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate
          ? a.createdAt.toDate()
          : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate
          ? b.createdAt.toDate()
          : new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setProjects(projectsData);
      setLoading(false);
    }
    fetchProjects();
  }, []);

  const showNotif = (msg) => {
    const div = document.createElement("div");
    div.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm";
    div.textContent = msg;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 3000);
  };

  const updateProjectField = async (id, field, value) => {
    setUpdatingId(id);
    try {
      const ref = doc(db, "projects", id);
      await updateDoc(ref, { [field]: value });
      setProjects((prev) =>
        prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
      );
      showNotif("Projet mis à jour !");
    } catch {
      alert("Erreur lors de la mise à jour");
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Confirmez-vous la suppression de ce projet ?"))
      return;
    try {
      await deleteDoc(doc(db, "projects", id));
      setProjects((p) => p.filter((x) => x.id !== id));
      showNotif("Projet supprimé !");
    } catch {
      alert("Erreur lors de la suppression");
    }
  };

  const filteredProjects = projects.filter((p) => {
    const statusMatch = filterStatus === "all" || p.status === filterStatus;
    const typeMatch = filterType === "all" || p.type === filterType;
    return statusMatch && typeMatch;
  });

  const statusCounts = {
    all: projects.length,
    pending: projects.filter((p) => p.status === "pending").length,
    confirmed: projects.filter((p) => p.status === "confirmed").length,
    waitPayment: projects.filter((p) => p.status === "waitPayment").length,
    paymentConfirmed: projects.filter((p) => p.status === "paymentConfirmed")
      .length,
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          {/* Filter tabs + type filter */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
              {[
                { key: "all", label: "Tous" },
                { key: "pending", label: "En attente" },
                { key: "confirmed", label: "Confirmés" },
                { key: "waitPayment", label: "Att. paiement" },
                { key: "paymentConfirmed", label: "Payés" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setFilterStatus(key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    filterStatus === key
                      ? "bg-gray-900 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {label} ({statusCounts[key]})
                </button>
              ))}
            </div>
            {categories.length > 0 && (
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
              >
                <option value="all">Tous les types</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            )}
            {(filterStatus !== "all" || filterType !== "all") && (
              <button
                onClick={() => { setFilterStatus("all"); setFilterType("all"); }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Réinitialiser
              </button>
            )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200">
            {filteredProjects.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Aucun projet trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Projet</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredProjects.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 font-medium text-gray-900">
                          <InlineEdit
                            value={p.project}
                            onSave={(val) => updateProjectField(p.id, "project", val)}
                            disabled={updatingId === p.id}
                          />
                        </td>
                        <td className="px-3 py-2.5 text-gray-600 text-xs">{p.clientEmail}</td>
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <select
                            disabled={updatingId === p.id}
                            value={BUDGET_OPTIONS.find(b => b.value === p.budget) ? p.budget : "custom"}
                            onChange={(e) => {
                              if (e.target.value !== "custom") {
                                updateProjectField(p.id, "budget", e.target.value);
                              }
                            }}
                            className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#801111] focus:border-[#801111] disabled:opacity-50"
                          >
                            {BUDGET_OPTIONS.map((b) => (
                              <option key={b.value} value={b.value}>{b.label}</option>
                            ))}
                            {p.budget && !BUDGET_OPTIONS.find(b => b.value === p.budget) && (
                              <option value="custom">{p.budget} € (personnalisé)</option>
                            )}
                          </select>
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap text-xs">
                          {p.createdAt && p.createdAt.toDate
                            ? moment(p.createdAt.toDate()).format("DD/MM/YYYY")
                            : p.createdAt || "-"}
                        </td>
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <select
                            disabled={updatingId === p.id}
                            value={p.status}
                            onChange={(e) => updateProjectField(p.id, "status", e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:opacity-50"
                          >
                            {Object.entries(STATUS_LABELS).map(([key, label]) => (
                              <option key={key} value={key}>{label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <select
                            disabled={updatingId === p.id}
                            value={p.type || ""}
                            onChange={(e) => updateProjectField(p.id, "type", e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:opacity-50"
                          >
                            <option value="">Sélectionner...</option>
                            {categories.map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2.5">
                          <button
                            disabled={updatingId === p.id}
                            onClick={(e) => { e.stopPropagation(); deleteProject(p.id); }}
                            className="text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}
